import React, { useState } from 'react';
import { Typography, Box, Grid2 } from '@mui/material';
import DatePicker from '../../../Input/DatePicker';
import Slots from '../Slots';
import { formatDateToYYYYMMDD } from '@utriper/core';
import { PickersDay } from '@mui/x-date-pickers';

interface Slot {
  start: string;
  startUTC: string;
  endUTC: string;
  end: string;
  timezone: string;
  currency: string;
  available: boolean;
  price?: string | number;
  id: number;
  duration: number;
}
interface SelectedSlot extends Slot {
  priceTotal?: string | number;
  timeOfArrivalFormatted?: string;
}


export interface ExperienceDatePickerProps {
  showPrice?: boolean;
  showAvailability?: boolean;
  slots: Slot[];
  quantity: number;
  datePicker: Date | null;
  experience: any;
  setSelectedSlot: (slot: SelectedSlot) => void;
  setDatePicker: React.Dispatch<React.SetStateAction<Date>>;
  setVisibleRange: (range: { start: Date; end: Date }) => void;
}

const ExperienceDatePicker: React.FC<ExperienceDatePickerProps> = ({
  showPrice = false,
  showAvailability = false,
  slots = [],
  quantity,
  datePicker,
  experience,
  setSelectedSlot,
  setDatePicker,
  setVisibleRange,
  ...props
}) => {
  const [selectedDate, setSelectedDate] = useState<string>('');

  const CustomDay: React.FC<any> = ({ selectedDay, ...other }) => {
    const dayFormatted = formatDateToYYYYMMDD(other.day);
    const slotsForDay = slots.filter(
      (slot) => slot.start?.substring(0, 10) === dayFormatted
    );

    setSelectedDate( formatDateToYYYYMMDD(selectedDay));
    
    let price: number | false = false;
    let availability = false;

    if (slotsForDay.length) {
      for (let i = 0; i < slotsForDay.length; i++) {
        const slot = slotsForDay[i];
        if (slot?.available === true) {
          availability = true;
        }
        if (slot?.price && (!price || parseFloat(slot?.price as string) > price)) {
          price = parseFloat(slot?.price as string);
        }
      }
    }
    return (
      <Grid2
        sx={
          availability
            ? {
                '& .MuiPickersDay-root': {
                  color: '#fff',
                  borderRadius: '18px',
                  borderWidth: '0px',
                  border: '0px solid',
                  backgroundColor: '#12a14b',
                },
              }
            : {
                '& .MuiPickersDay-root': {
                  color: '#7d7987',
                  fontWeight: '500 !important',
                  fontSize: '12px !important',
                  background: 'transparent',
                },
              }
        }>
        <Grid2>
          <PickersDay {...other} />
        </Grid2>
        {showPrice ? (
          <Typography sx={{ fontSize: 7.5 }}>
            {price ? `$${price}` : '\u00A0'}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 7.5 }}>&nbsp;</Typography>
        )}
      </Grid2>
    );
  };

  return (
    <Grid2
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        height: '100vh',
      }}>
      <Box
        sx={{
          marginTop: '20px',
          border: '1px solid rgba(0, 0, 0, 0.23)',
          borderRadius: '4px',
          '& .MuiDayCalendar-weekDayLabel': {
            color: '#7d7987',
            fontWeight: '500 !important',
            fontSize: '12px !important',
            background: 'transparent',
          },
          '& .Mui-selected': {
            color: '#7d7987',
            borderRadius: '18px',
            backgroundColor: 'lightblue !important',
            '&:hover': {
              backgroundColor: 'lightblue',
            },
          },
        }}>
        <DatePicker
          slots={{ day: CustomDay }}
          value={datePicker}
          onChange={(date) => setDatePicker(date || new Date())}
          onMonthChange={setDatePicker}
          setVisibleRange={setVisibleRange}
          slotProps={{
            actionBar: {
              actions: [],
            },
            toolbar: {
              sx: { display: 'none' },
            },
            calendarHeader: {
              disabled: true,
            },
            day: { selectedDay: datePicker },
          }}
          {...props}
        />
      </Box>
      <Box
        sx={{
          maxHeight: '16rem',
          overflowY: 'auto',
          width: '100%',
          '&::-webkit-scrollbar': {
            width: '2px',
          },
          '&::-webkit-scrollbar-track': {
            boxShadow: 'inset 0 0 6px rgba(0,0,0,0.3)',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'darkgrey',
            outline: '1px solid slategrey',
          },
          '& scrollbar-color': 'darkgrey lightgrey',
          '& scrollbar-width': 'thin',
        }}>
        <Slots
          experience={experience}
          quantity={quantity}
          slot={slots}
          daySelected={selectedDate}
          setSelectedSlot={setSelectedSlot}
        />
      </Box>
    </Grid2>
  );
};

export default ExperienceDatePicker;
